import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { formatPrice } from '../../../data/modules';
import { useModuleCatalog } from '../../../data/useModuleCatalog';

const SIGNUP_URL = 'https://panel.cafeotomasyonu.com/sign-up';

const PricingSection = () => {
  const { purchasableModules, vatIncluded } = useModuleCatalog();
  // 'monthly' | 'yearly' — yıllık ödemede 12 ay yerine 10 ay ücreti alınıyor.
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const isYearly = billingPeriod === 'yearly';

  return (
    <section id="fiyatlandirma" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-poppins font-heading-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
            Şeffaf{' '}
            <span className="text-primary">Fiyatlandırma</span>
          </h2>
          <p className="font-open-sans font-body-normal text-lg text-text-secondary max-w-3xl mx-auto">
            Paket yok, sürpriz yok. Her modülün fiyatı ayrı; yalnızca kullandığınız modülü ödersiniz.
            QR Menü sürekli ücretsiz, diğerleri 30 gün (Yapay Zeka 15 gün) ücretsiz denenebilir.
          </p>
        </div>

        {/* Ödeme Dönemi Seçici */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center p-1 bg-white rounded-full border border-gray-200 shadow-sm">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-inter font-accent-semibold transition-all duration-300 ${
                !isYearly ? 'bg-primary text-white shadow' : 'text-text-secondary hover:text-foreground'
              }`}
            >
              Aylık
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-inter font-accent-semibold transition-all duration-300 flex items-center space-x-2 ${
                isYearly ? 'bg-primary text-white shadow' : 'text-text-secondary hover:text-foreground'
              }`}
            >
              <span>Yıllık</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                isYearly ? 'bg-white/20 text-white' : 'bg-green-100 text-green-700'
              }`}>
                2 ay hediye
              </span>
            </button>
          </div>
        </div>

        {/* Modül Fiyat Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {purchasableModules.map((module) => {
            const amount = isYearly ? module.price.yearly : module.price.monthly;
            const isFree = module.price.monthly === 0;

            return (
              <div
                key={module.id}
                /* Arayüz koşusu tutarı BURADAN okuyor: ekranda "1.199 ₺" yazan
                   metni ayrıştırmak yerine ham sayıyı karşılaştırıyor, böylece
                   biçimlendirme değiştiğinde test sahte kırmızı vermiyor. */
                data-price-card={module.id}
                data-module-name={module.moduleName}
                data-price-amount={amount}
                className={`relative flex flex-col bg-white rounded-2xl p-6 border transition-all duration-300 hover:shadow-xl ${
                  module.popular ? 'border-primary shadow-lg lg:scale-[1.02]' : 'border-gray-200 shadow-sm'
                }`}
              >
                {module.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-primary text-white text-xs font-inter font-accent-semibold whitespace-nowrap">
                    En çok tercih edilen
                  </span>
                )}

                {/* Başlık */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${module.color}`}>
                    <Icon name={module.icon} size={24} className="text-white" />
                  </div>
                  {module.badge && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      module.badge === 'ÜCRETSİZ' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {module.badge}
                    </span>
                  )}
                </div>

                <h3 className="font-poppins font-heading-semibold text-lg text-foreground mb-1">
                  {module.name}
                </h3>
                <p className="font-open-sans font-body-normal text-xs text-text-secondary mb-4">
                  {module.subtitle}
                </p>

                {/* Fiyat */}
                <div className="mb-4 pb-4 border-b border-gray-100">
                  <div className="flex items-baseline space-x-1">
                    <span className="font-poppins font-heading-bold text-3xl text-foreground">
                      {formatPrice(amount)}
                    </span>
                    {!isFree && (
                      <span className="font-open-sans font-body-normal text-sm text-text-secondary">
                        /{isYearly ? 'yıl' : 'ay'}
                      </span>
                    )}
                  </div>
                  <p className="font-inter font-accent-medium text-xs mt-2">
                    {isFree ? (
                      <span className="text-green-600">Süresiz ücretsiz, kredi kartı istemez</span>
                    ) : (
                      <span className="text-blue-600">{module.trialDays} gün ücretsiz deneme</span>
                    )}
                  </p>
                </div>

                {/* Özellikler */}
                <ul className="space-y-2 mb-6 flex-1">
                  {module.features.slice(0, 5).map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name="Check" size={10} className="text-green-600" />
                      </div>
                      <span className="font-open-sans font-body-normal text-xs text-text-secondary leading-relaxed">
                        {feature}
                      </span>
                    </li>
                  ))}
                  {module.features.length > 5 && module.path && (
                    <li>
                      <Link
                        to={module.path}
                        className="font-inter font-accent-medium text-xs text-primary hover:underline"
                      >
                        +{module.features.length - 5} özellik daha
                      </Link>
                    </li>
                  )}
                </ul>

                {/* CTA */}
                <a
                  href={SIGNUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full inline-flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-inter font-accent-semibold text-sm transition-colors duration-300 ${
                    module.popular || isFree
                      ? 'bg-primary text-white hover:bg-primary/90'
                      : 'border border-primary text-primary hover:bg-primary/5'
                  }`}
                >
                  <span>{isFree ? 'Ücretsiz Başla' : 'Ücretsiz Dene'}</span>
                  <Icon name="ArrowRight" size={16} />
                </a>
              </div>
            );
          })}
        </div>

        {/* Fiyat notu — KDV ve katalog gerçeği */}
        <p className="text-center font-open-sans font-body-normal text-xs text-text-secondary mt-8">
          Fiyatlara KDV {vatIncluded ? 'dahildir' : 'dahil değildir'}. Modülleri istediğiniz zaman tek tek açıp kapatabilirsiniz;
          kurulum ve güncelleme ücreti alınmaz.
        </p>

        {/* Alt Şerit */}
        <div className="mt-16">
          <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="font-poppins font-heading-bold text-2xl mb-4">
                Önce deneyin, sonra karar verin
              </h3>
              <p className="font-open-sans font-body-normal text-lg opacity-90 mb-8">
                Kayıt olurken kredi kartı istemiyoruz. Deneme süresi bittiğinde modül kendiliğinden kapanır,
                menünüz ücretsiz çalışmaya devam eder.
              </p>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 text-left">
                {[
                  { icon: 'Zap', title: 'Dakikalar içinde kurulum', note: 'Kayıt olun, menünüzü girin, yayına alın' },
                  { icon: 'WifiOff', title: 'Çevrimdışı çalışır', note: 'İnternet kesilse de satış durmaz' },
                  { icon: 'RefreshCw', title: 'Güncellemeler dahil', note: 'Yeni özellikler ek ücret istemez' },
                  { icon: 'Headphones', title: 'Gerçek insan desteği', note: 'WhatsApp ve e-posta ile' }
                ].map((item) => (
                  <div key={item.title} className="flex items-start space-x-3">
                    <Icon name={item.icon} size={20} color="white" className="flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-inter font-accent-semibold text-sm">{item.title}</p>
                      <p className="font-open-sans font-body-normal text-xs opacity-80">{item.note}</p>
                    </div>
                  </div>
                ))}
              </div>

              <a
                href={SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-white text-primary px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors duration-300 font-poppins font-cta-semibold"
              >
                <Icon name="ExternalLink" size={20} />
                <span>Hemen Ücretsiz Kayıt Ol</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
